<template>
	<v-form @submit.prevent="handleForm">
		<v-row>
			<v-col cols="12" md="8">
				<v-text-field
					v-model="form.name"
					label="Project Name"
					name="name"
					:error="hasErrors(form, 'name')"
					:hide-details="! hasErrors(form, 'name')"
					:error-messages="errorMsg(form, 'name')"
					persistent-placeholder
					autofocus
					outlined
					required
					dense />
			</v-col>
			<v-col>
				<v-menu
					v-model="datePicker"
					:close-on-content-click="false"
					transition="scale-transition"
					offset-y
					min-width="auto">
					<template #activator="{ on, attrs }">
						<v-text-field
							:value="form.due_date | localDate"
							label="Due Date"
							append-icon="mdi-calendar"
							:error="hasErrors(form, 'due_date')"
							:hide-details="! hasErrors(form, 'due_date')"
							:error-messages="errorMsg(form, 'due_date')"
							persistent-placeholder
							readonly
							outlined
							v-bind="attrs"
							dense
							v-on="on"></v-text-field>
					</template>
					<v-date-picker
						v-model="form.due_date"
						no-title
						scrollable
						class="pb-4"
						@input="datePicker = false"></v-date-picker>
				</v-menu>
			</v-col>
		</v-row>
		<v-row>
			<v-col cols="12" md="4">
				<v-autocomplete
					v-model="form.client_id"
					:items="clients"
					:loading="! length(clients)"
					:error="hasErrors(form, 'client_id')"
					:hide-details="! hasErrors(form, 'client_id')"
					:error-messages="errorMsg(form, 'client_id')"
					persistent-placeholder
					label="Select Client"
					placeholder="Type to search clients"
					item-text="name"
					item-value="id"
					filled
					eager
					dense
					outlined
					clearable
					required>
				</v-autocomplete>
			</v-col>
			<v-col cols="12" md="4">
				<v-autocomplete
					v-model="form.orderer_id"
					:disabled="! Object.keys(orderers).length"
					:items="orderers"
					:error="hasErrors(form, 'orderer_id')"
					:hide-details="! hasErrors(form, 'orderer_id')"
					:error-messages="errorMsg(form, 'orderer_id')"
					persistent-placeholder
					label="Orderer"
					placeholder="Select Orderer"
					item-text="name"
					item-value="id"
					filled
					outlined
					clearable
					required
					dense>
				</v-autocomplete>
			</v-col>
			<v-col cols="12" md="4">
				<v-autocomplete
					v-model="form.reviewer_id"
					:disabled="! Object.keys(managers).length"
					:items="managers"
					:error="hasErrors(form, 'reviewer_id')"
					:hide-details="! hasErrors(form, 'reviewer_id')"
					:error-messages="errorMsg(form, 'reviewer_id')"
					persistent-placeholder
					label="Manager"
					placeholder="Select Manager"
					item-text="name"
					item-value="id"
					filled
					outlined
					clearable
					required
					dense>
				</v-autocomplete>
			</v-col>
		</v-row>
		<v-row>
			<v-col cols="12" md="3">
				<v-select
					v-model="form.type"
					:items="types"
					:error="hasErrors(form, 'type')"
					:hide-details="! hasErrors(form, 'type')"
					:error-messages="errorMsg(form, 'type')"
					persistent-placeholder
					outlined
					name="type"
					label="Project Type"
					required
					dense></v-select>
			</v-col>
			<v-col cols="12" md="3">
				<v-select
					v-model="form.reason"
					:items="reasons"
					:error="hasErrors(form, 'reason')"
					:hide-details="! hasErrors(form, 'reason')"
					:error-messages="errorMsg(form, 'reason')"
					persistent-placeholder
					outlined
					name="reason"
					label="Reason for job"
					dense></v-select>
			</v-col>
			<v-col cols="12" md="3">
				<v-select
					v-model="form.sector"
					:items="sectors"
					:error="hasErrors(form, 'sector')"
					:hide-details="! hasErrors(form, 'sector')"
					:error-messages="errorMsg(form, 'sector')"
					persistent-placeholder
					outlined
					name="sector"
					label="Sector"
					dense></v-select>
			</v-col>
			<v-col cols="12" md="3">
				<v-select
					v-model="form.activity"
					:items="activities"
					:error="hasErrors(form, 'activity')"
					:hide-details="! hasErrors(form, 'activity')"
					:error-messages="errorMsg(form, 'activity')"
					persistent-placeholder
					outlined
					name="activity"
					label="Activity"
					dense></v-select>
			</v-col>
		</v-row>
		<v-row>
			<v-col>
				<v-textarea
					v-model="form.notes"
					name="notes"
					filled
					outlined
					label="Notes"
					auto-grow
					:error="hasErrors(form, 'notes')"
					:hide-details="! hasErrors(form, 'notes')"
					:error-messages="errorMsg(form, 'notes')"
					persistent-placeholder
					dense />
			</v-col>
		</v-row>
		<v-row>
			<v-col>
				<v-divider class="mt-0 mb-5" />
				<v-btn type="submit"
					color="success"
					large
					:loading="form.processing"
					v-text="buttonLabel" />
				<delete-button
					v-if="project.id"
					classes="float-right"
					plain
					x-small
					warning="This will permanently delete everything associated with this project. IE: Properties, Files, Reports, Invoices"
					model="projects"
					:model-id="project.id">
					Delete Project
				</delete-button>
			</v-col>
		</v-row>
	</v-form>
</template>
<script>
import api from '@/api'
import DeleteButton from '@/Pages/Backend/Components/DeleteButton'


export default {
	components: { DeleteButton },
	remember: {
		data: ['form'],
		key () {
			return this.project.id
				? `Projects/Edit:${this.project.id}`
				: 'Projects/New'
		}
	},
	props: {
		project: {
			required: false,
			type: Object,
			default: () => {
				return {
					id: null,
					name: null,
					type: null,
					reason: null,
					sector: null,
					activity: null,
					notes: null,
					due_date: null,
					client_id: null,
					orderer_id: null,
					reviewer_id: null
				}
			}
		},
		client: {
			type: Object,
			required: false,
			default: null
		}
	},
	data () {
		return {
			orderers: [],
			sectors: [],
			activities: [],
			types: [],
			managers: [],
			reasons: [],
			clients: [],
			datePicker: null,
			formValid: true,
			form: this.$inertia.form({
				name: this.project.name,
				type: this.project.type,
				reason: this.project.reason,
				sector: this.project.sector,
				activity: this.project.activity,
				notes: this.project.notes,
				due_date: this.project.due_date,
				client_id: this.client ? this.client.id : null,
				orderer_id: this.project.orderer_id,
				reviewer_id: this.project.reviewer_id
			})
		}
	},
	computed: {
		buttonLabel () {
			if (this.project.id) {
				return 'Update Project'
			}

			return 'Create Project'
		}
	},
	watch: {
		'form.client_id' (clientId) {
			this.orderers = []
			this.user = {}

			if (clientId) {
				this.getUsers()
			}
		}
	},
	mounted () {
		this.getClients({ active: 'true' })
		const method = 'GET'
		const data = {}

		const url = this.route('projects.api.options')

		api({method, url, data}).then(response => {
			const options = response.data
			this.types = options.types
			this.reasons = options.reasons
			this.sectors = options.sectors
			this.activities = options.activities
			this.managers = options.managers
		})

		const client = this.$page.props.client
		if (client) {
			this.form.client_id = client.id
		}

		const orderer = this.project.orderer
		if (orderer) {
			this.user = {
				id: orderer.id,
				name: orderer.name
			}

			this.form.orderer_id = orderer.id
			this.getUsers()
		}
	},
	methods: {
		getUsers () {
			const method = 'POST'
			const data = { client_id: this.form.client_id }
			const url = this.route('users.api.index')

			api({
				method,
				url,
				data
			}).then(response => {
				this.orderers = response.data
			})
		},
		handleForm () {
			if (this.project.id) {
				return this.form.put(this.route('projects.update', this.project))
			}

			return this.form.post(this.route('projects.store'))
		}
	}
}
</script>
